import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jk-core/utils';
import styles from './DropDown.module.scss';
import SearchIcon from './search.svg';

interface ListProps {
  selectedItem?: string;
  list: string[];
  filteredList: string[];
  parent: React.RefObject<HTMLDivElement | null>;
  onSelect: (item: string, index: number) => void;
  setFilteredList?: React.Dispatch<React.SetStateAction<string[]>>
  ;
}

interface Position {
  top?: number;
  left?: number;
  bottom?: number;
  height: number;
}

export default function List({ parent, selectedItem, list, filteredList, onSelect, setFilteredList }: ListProps) {
  const listHeight = useMemo(() => {
    if (setFilteredList) {
      return Math.min((filteredList.length * 50) + 40, 300);
    }
    return Math.min(list.length * 50, 300);
  }, [filteredList.length, list.length, setFilteredList]);

  const listRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>(() => {
    if (!parent || !parent.current) {
      return { top: 0, left: 0, bottom: 0, height: 0 };
    }
    const rect = parent.current.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow < listHeight && spaceAbove > listHeight) {
      // 위에 열림: bottom 사용, top은 undefined
      return {
        top: undefined,
        left: rect.left + window.scrollX,
        bottom: window.innerHeight - rect.top - window.scrollY,
        height: 0,
      };
    }
    // 아래에 열림: top 사용, bottom은 undefined
    return {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      bottom: undefined,
      height: 0,
      scroll: window.scrollY,
    };
  });

  useEffect(() => {
    function updatePosition() {
      setPosition(prev => ({
        ...prev,
        height: listHeight,
      }));
    }

    updatePosition();

    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [listHeight]);

  useEffect(() => {
    const handleScroll = () => {
      if (!parent || !parent.current) return;
      const rect = parent.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < listHeight && spaceAbove > listHeight) {
      // 위에 열림
        setPosition({
          top: undefined,
          left: rect.left + window.scrollX,
          bottom: window.innerHeight - rect.top - window.scrollY,
          height: listHeight,
        });
      } else {
      // 아래에 열림
        setPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          bottom: undefined,
          height: listHeight,
        });
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll, true);
    };
  }, [listHeight, parent, position.height]);

  return (
    <div
      className={cn({
        [styles['list-wrapper']]: true,
        [styles['list-wrapper--up']]: position.bottom === undefined,
        [styles['list-wrapper--down']]: position.top === undefined,
      })}
      style={{
        width: parent?.current?.offsetWidth,
        height: position.height,
        top: position.top,
        left: position.left,
        bottom: position.bottom,
      }}
    >
      <div className={styles.list} ref={listRef}>
        {setFilteredList && (
          <label className={styles.list__search}>
            <SearchIcon />
            <input
              type="text"
              placeholder="검색"
              onChange={(e) => setFilteredList(list.filter((item) => item.includes(e.target.value)))}
            />
          </label>
        )}
        {filteredList.length === 0 && (
          <div className={styles.list__empty}>
            검색 결과가 없습니다.
          </div>
        )}
        {filteredList.map((item, index) => (
          <button
            className={cn({
              [styles.list__item]: true,
              [styles['list__item--selected']]: item === selectedItem,
            })}
            type="button"
            key={item + index}
            onClick={() => onSelect(item, index)}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}